"use client";

import { useMemo, useState } from "react";
import {
  DataTable,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  DensityChart,
  Probability,
} from "@/components/tools/stats-distributions/charts";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { normalPdf } from "@/lib/stats/distributions";
import { densityGeometry } from "@/lib/tool-kit/stats-distributions/chart";
import type { NumberFormatter } from "@/lib/tool-kit/stats-distributions/format";
import {
  type InverseResult,
  isValidNormal,
  type NormalRegion,
  normalInverse,
  normalRegion,
  type RegionResult,
  sketchBounds,
} from "@/lib/tool-kit/stats-distributions/normal";

/** English strings. A translated content file overrides these through `ui`. */
export const NORMAL_DISTRIBUTION_CALCULATOR_STRINGS = {
  meanLabel: "Mean (μ)",
  sdLabel: "Standard deviation (σ)",
  findLabel: "Find",
  findProbability: "A probability",
  findValue: "A value from a probability",
  regionLabel: "Probability of",
  regionBelow: "Below x — P(X < x)",
  regionAbove: "Above x — P(X > x)",
  regionBetween: "Between a and b — P(a < X < b)",
  regionOutside: "Outside a and b — P(X < a or X > b)",
  xLabel: "Value (x)",
  aLabel: "Lower value (a)",
  bLabel: "Upper value (b)",
  pLabel: "Probability (between 0 and 1)",
  results: "Results",
  probability: "Probability",
  percent: "As a percentage",
  zScore: "z-score",
  zScores: "z-scores",
  workingHeading: "How this was calculated",
  sketchLabel:
    "Normal curve with mean {mean} and SD {sd}, the area for the answer shaded",
  sketchCaption:
    "Normal distribution, μ = {mean}, σ = {sd}. The shaded area is the probability.",
  inverseColumnQuestion: "Question",
  inverseColumnAnswer: "Answer",
  inverseColumnZ: "z",
  inverseLeft: "x with P(X < x) = {p}",
  inverseRight: "x with P(X > x) = {p}",
  inverseCentral: "Central interval holding {p}",
  inverseCaption:
    "The shaded area is P(X < x) = {p}; the dashed lines mark the central interval.",
  enterParameters: "Enter a mean and a standard deviation greater than 0.",
  enterX: "Enter the value of x.",
  enterBounds: "Enter both a and b.",
  enterP: "Enter a probability strictly between 0 and 1, such as 0.95.",
} as const;

type Strings = typeof NORMAL_DISTRIBUTION_CALCULATOR_STRINGS;
type Find = "probability" | "value";

interface Form {
  mean: string;
  sd: string;
  find: Find;
  region: NormalRegion;
  x: string;
  a: string;
  b: string;
  p: string;
}

const INITIAL: Form = {
  mean: "100",
  sd: "15",
  find: "probability",
  region: "below",
  x: "115",
  a: "85",
  b: "115",
  p: "0.95",
};

const TWO_BOUNDS: ReadonlySet<NormalRegion> = new Set(["between", "outside"]);

const four = (fmt: NumberFormatter, value: number) =>
  fmt(value, { maximumFractionDigits: 4 });

function regionNotation(
  region: NormalRegion,
  low: string,
  high: string
): string {
  if (region === "below") {
    return `P(X < ${low})`;
  }
  if (region === "above") {
    return `P(X > ${low})`;
  }
  if (region === "between") {
    return `P(${low} < X < ${high})`;
  }
  return `P(X < ${low}) + P(X > ${high})`;
}

function Sketch({
  mu,
  sigma,
  shaded,
  markers,
  caption,
  label,
  fmt,
}: {
  mu: number;
  sigma: number;
  shaded: [number, number][];
  markers: number[];
  caption: string;
  label: string;
  fmt: NumberFormatter;
}) {
  const geometry = useMemo(() => {
    const [xMin, xMax] = sketchBounds(mu, sigma, markers);
    return densityGeometry({
      pdf: (x) => normalPdf(x, mu, sigma),
      xMin,
      xMax,
      shaded,
      markers,
    });
  }, [mu, sigma, shaded, markers]);
  return (
    <div className={`${CARD} space-y-3`}>
      <DensityChart
        format={(value) => fmt(value, { maximumFractionDigits: 2 })}
        geometry={geometry}
        label={label}
      />
      <p className="text-muted-foreground text-sm">{caption}</p>
    </div>
  );
}

function RegionResults({
  form,
  mu,
  sigma,
  result,
  strings,
  fmt,
}: {
  form: Form;
  mu: number;
  sigma: number;
  result: RegionResult;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const two = result.zHigh !== null;
  const bounds = result.shaded.flat().filter(Number.isFinite);
  const low = two ? Math.min(...bounds) : bounds[0];
  const high = two ? Math.max(...bounds) : low;
  const notation = regionNotation(form.region, four(fmt, low), four(fmt, high));
  const zLow = four(fmt, result.zLow);
  const zHigh = result.zHigh === null ? "" : four(fmt, result.zHigh);
  const parameters = { mean: four(fmt, mu), sd: four(fmt, sigma) };
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={notation}
          value={<Probability fmt={fmt} value={result.probability} />}
        />
        <StatCard
          label={strings.percent}
          value={`${fmt(result.probability * 100, { maximumFractionDigits: 2 })}%`}
        />
        <StatCard
          label={two ? strings.zScores : strings.zScore}
          value={two ? `${zLow}, ${zHigh}` : zLow}
        />
      </StatGrid>
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <p className="font-mono text-muted-foreground text-sm">
          z = (x − μ) / σ = ({four(fmt, low)} − {parameters.mean}) /{" "}
          {parameters.sd} = {zLow}
        </p>
        {two ? (
          <p className="font-mono text-muted-foreground text-sm">
            z = ({four(fmt, high)} − {parameters.mean}) / {parameters.sd} ={" "}
            {zHigh}
          </p>
        ) : null}
        <p className="font-mono text-muted-foreground text-sm">
          {notation} = {workingTail(form.region, zLow, zHigh)} ={" "}
          <Probability fmt={fmt} value={result.probability} />
        </p>
      </div>
      <Sketch
        caption={t(strings.sketchCaption, parameters)}
        fmt={fmt}
        label={t(strings.sketchLabel, parameters)}
        markers={two ? [low, high] : [low]}
        mu={mu}
        shaded={result.shaded}
        sigma={sigma}
      />
    </>
  );
}

function workingTail(
  region: NormalRegion,
  zLow: string,
  zHigh: string
): string {
  if (region === "below") {
    return `Φ(${zLow})`;
  }
  if (region === "above") {
    return `1 − Φ(${zLow})`;
  }
  if (region === "between") {
    return `Φ(${zHigh}) − Φ(${zLow})`;
  }
  return `Φ(${zLow}) + 1 − Φ(${zHigh})`;
}

function InverseResults({
  p,
  mu,
  sigma,
  result,
  strings,
  fmt,
}: {
  p: number;
  mu: number;
  sigma: number;
  result: InverseResult;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const pText = fmt(p, { maximumFractionDigits: 6 });
  const parameters = { mean: four(fmt, mu), sd: four(fmt, sigma) };
  return (
    <>
      <DataTable
        head={[
          strings.inverseColumnQuestion,
          strings.inverseColumnAnswer,
          strings.inverseColumnZ,
        ]}
        rows={[
          [
            t(strings.inverseLeft, { p: pText }),
            `x = ${four(fmt, result.left)}`,
            four(fmt, result.zLeft),
          ],
          [
            t(strings.inverseRight, { p: pText }),
            `x = ${four(fmt, result.right)}`,
            four(fmt, -result.zLeft),
          ],
          [
            t(strings.inverseCentral, { p: pText }),
            `${four(fmt, result.centralLow)} – ${four(fmt, result.centralHigh)}`,
            `± ${four(fmt, result.zCentral)}`,
          ],
        ]}
      />
      <Sketch
        caption={`${t(strings.sketchCaption, parameters)} ${t(strings.inverseCaption, { p: pText })}`}
        fmt={fmt}
        label={t(strings.sketchLabel, parameters)}
        markers={[result.centralLow, result.centralHigh]}
        mu={mu}
        shaded={[[Number.NEGATIVE_INFINITY, result.left]]}
        sigma={sigma}
      />
    </>
  );
}

function Inputs({
  form,
  update,
  strings,
}: {
  form: Form;
  update: (patch: Partial<Form>) => void;
  strings: Strings;
}) {
  if (form.find === "value") {
    return (
      <div className="max-w-xs">
        <TextField
          id="nd-p"
          label={strings.pLabel}
          numeric
          onChange={(p) => update({ p })}
          value={form.p}
        />
      </div>
    );
  }
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <SelectField
        id="nd-region"
        label={strings.regionLabel}
        onChange={(region) => update({ region })}
        options={[
          { value: "below", label: strings.regionBelow },
          { value: "above", label: strings.regionAbove },
          { value: "between", label: strings.regionBetween },
          { value: "outside", label: strings.regionOutside },
        ]}
        value={form.region}
      />
      {TWO_BOUNDS.has(form.region) ? (
        <>
          <TextField
            id="nd-a"
            label={strings.aLabel}
            numeric
            onChange={(a) => update({ a })}
            value={form.a}
          />
          <TextField
            id="nd-b"
            label={strings.bLabel}
            numeric
            onChange={(b) => update({ b })}
            value={form.b}
          />
        </>
      ) : (
        <TextField
          id="nd-x"
          label={strings.xLabel}
          numeric
          onChange={(x) => update({ x })}
          value={form.x}
        />
      )}
    </div>
  );
}

function Results({
  form,
  strings,
  fmt,
}: {
  form: Form;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const mu = parseNumberInput(form.mean);
  const sigma = parseNumberInput(form.sd);
  if (mu === null || sigma === null || !isValidNormal(mu, sigma)) {
    return <Notice>{strings.enterParameters}</Notice>;
  }
  if (form.find === "value") {
    const p = parseNumberInput(form.p);
    const result = p === null ? null : normalInverse(p, mu, sigma);
    if (p === null || result === null) {
      return <Notice>{strings.enterP}</Notice>;
    }
    return (
      <InverseResults
        fmt={fmt}
        mu={mu}
        p={p}
        result={result}
        sigma={sigma}
        strings={strings}
      />
    );
  }
  const two = TWO_BOUNDS.has(form.region);
  const a = parseNumberInput(two ? form.a : form.x);
  const b = two ? parseNumberInput(form.b) : null;
  if (a === null || (two && b === null)) {
    return <Notice>{two ? strings.enterBounds : strings.enterX}</Notice>;
  }
  const result = normalRegion(form.region, mu, sigma, a, b);
  if (!result) {
    return <Notice>{strings.enterBounds}</Notice>;
  }
  return (
    <RegionResults
      fmt={fmt}
      form={form}
      mu={mu}
      result={result}
      sigma={sigma}
      strings={strings}
    />
  );
}

export default function NormalDistributionCalculatorWidget() {
  const strings = useToolUi(NORMAL_DISTRIBUTION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [form, setForm] = useState<Form>(INITIAL);
  const update = (patch: Partial<Form>) =>
    setForm((current) => ({ ...current, ...patch }));

  return (
    <>
      <section className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="nd-mean"
            label={strings.meanLabel}
            numeric
            onChange={(mean) => update({ mean })}
            value={form.mean}
          />
          <TextField
            id="nd-sd"
            label={strings.sdLabel}
            numeric
            onChange={(sd) => update({ sd })}
            value={form.sd}
          />
        </div>
        <PillGroup
          label={strings.findLabel}
          name="nd-find"
          onChange={(find) => update({ find })}
          options={[
            { value: "probability", label: strings.findProbability },
            { value: "value", label: strings.findValue },
          ]}
          value={form.find}
        />
        <Inputs form={form} strings={strings} update={update} />
      </section>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Results fmt={fmt} form={form} strings={strings} />
      </section>
    </>
  );
}
